import React, { useCallback, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IconButton, Tooltip } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import CloseIcon from '@material-ui/icons/Close';
import RestoreIcon from '@material-ui/icons/Restore';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import ProductSelector from 'pluginAdmin/features/productSelector/ProductSelector';
import SafeProductImage from 'components/kea/SafeProductImage';
import orderedCollectionStyles from './orderedCollectionStyles';
import AutoFillControl from './AutoFillControl';
import PinAction from './PinAction';
import useOrderedCollectionMotion from './useOrderedCollectionMotion';

const S = {
  ...orderedCollectionStyles,
  searchArea: {
    padding: '12px 20px',
    borderBottom: '1px solid #edf2f0',
    background: '#fbfefd',
  },
  row: {
    ...orderedCollectionStyles.row,
    gridTemplateColumns: '32px 48px minmax(0, 1fr) auto 36px 36px',
  },
  thumb: {
    width: 48,
    height: 48,
    borderRadius: 6,
    objectFit: 'cover',
    background: '#f1f3f6',
  },
  thumbEmpty: {
    display: 'flex',
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    background: '#f1f3f6',
    color: '#8790a5',
  },
  price: {
    color: '#1e2248',
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  limitControl: {
    display: 'inline-flex',
    minHeight: 36,
    alignItems: 'center',
    gap: 8,
    color: '#66708a',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  limitInput: {
    width: 68,
    minHeight: 36,
    boxSizing: 'border-box',
    padding: '6px 9px',
    border: '1px solid #d5e1dd',
    borderRadius: 8,
    background: '#fff',
    color: '#1e2248',
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  warningBadge: {
    display: 'inline-flex',
    minHeight: 22,
    alignItems: 'center',
    padding: '1px 7px',
    border: '1px solid #efdfbd',
    borderRadius: 999,
    background: '#fff9ec',
    color: '#9a6a1f',
    fontSize: 11,
    fontWeight: 600,
  },
};

const formatPrice = (price) => {
  if (price == null || Number(price) <= 0) return '';
  return Number(price).toFixed(2);
};

const ProductThumb = ({ product, blocked = false }) => (
  <SafeProductImage
    src={product.thumbnail}
    alt={product.name}
    style={{
      ...(product.thumbnail ? S.thumb : S.thumbEmpty),
      ...(blocked ? S.hiddenMedia : null),
    }}
  />
);

const ProductsEditor = ({
  pinnedProducts = [],
  resolvedProducts = [],
  excludedProductIds = [],
  generatedProducts = [],
  onSearch,
  onChange,
  onExcludedChange = () => {},
  limit = 16,
  onLimitChange = () => {},
  autoFill = true,
  onAutoFillChange = () => {},
  hideHeader = false,
}) => {
  const t = useLocalizedMessage();
  const [hoveredId, setHoveredId] = useState(null);
  const excludedIds = useMemo(
    () => new Set(excludedProductIds.map((id) => `${id}`)),
    [excludedProductIds],
  );

  const effectiveItems = useMemo(() => {
    const seen = new Set();
    const manual = [];
    pinnedProducts.forEach((product) => {
      const key = `${product.id}`;
      if (seen.has(key) || manual.length >= limit) return;
      manual.push({ ...product, key, manual: true });
      seen.add(key);
    });

    const automatic = [];
    if (autoFill) generatedProducts.forEach((product) => {
      const key = `${product.id}`;
      if (
        seen.has(key)
        || excludedIds.has(key)
        || manual.length + automatic.length >= limit
      ) return;
      automatic.push({ ...product, key, manual: false });
      seen.add(key);
    });
    return [...manual, ...automatic];
  }, [autoFill, excludedIds, generatedProducts, limit, pinnedProducts]);
  const { containerRef: listMotionRef, animateNextChange } = useOrderedCollectionMotion(
    effectiveItems.map(product => product.key),
  );

  const knownProducts = useMemo(() => {
    const byId = new Map();
    [...generatedProducts, ...pinnedProducts, ...resolvedProducts].forEach((product) => {
      byId.set(`${product.id}`, product);
    });
    return byId;
  }, [generatedProducts, pinnedProducts, resolvedProducts]);

  const searchApi = useMemo(() => ({
    async searchProducts(query, searchLimit = 8) {
      const items = await onSearch(query, searchLimit);
      return items || [];
    },
  }), [onSearch]);

  const handleAdd = useCallback((product) => {
    const key = `${product.id}`;
    if (effectiveItems.some((item) => item.key === key)) return;
    if (excludedIds.has(key)) {
      onExcludedChange(excludedProductIds.filter((id) => `${id}` !== key));
    }
    onChange([...pinnedProducts, product]);
  }, [effectiveItems, excludedIds, excludedProductIds, onChange, onExcludedChange, pinnedProducts]);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const moved = effectiveItems[result.source.index];
    if (!moved) return;
    if (moved.manual) {
      const nextPinned = [...pinnedProducts];
      const [product] = nextPinned.splice(result.source.index, 1);
      const destinationIndex = Math.min(result.destination.index, nextPinned.length);
      if (destinationIndex !== result.destination.index) {
        animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
      }
      nextPinned.splice(destinationIndex, 0, product);
      onChange(nextPinned);
      return;
    }
    const product = { ...moved };
    delete product.key;
    delete product.manual;
    const nextPinned = [...pinnedProducts];
    const destinationIndex = Math.min(result.destination.index, nextPinned.length);
    if (destinationIndex !== result.destination.index) {
      animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
    }
    nextPinned.splice(destinationIndex, 0, product);
    onChange(nextPinned);
  }, [animateNextChange, effectiveItems, onChange, pinnedProducts]);

  const handlePin = useCallback((product) => {
    const key = `${product.id}`;
    animateNextChange();
    if (product.manual) {
      onChange(pinnedProducts.filter((candidate) => `${candidate.id}` !== key));
      return;
    }
    const plainProduct = { ...product };
    delete plainProduct.key;
    delete plainProduct.manual;
    onChange([...pinnedProducts, plainProduct]);
  }, [animateNextChange, onChange, pinnedProducts]);

  const handleHide = useCallback((product) => {
    const key = `${product.id}`;
    const nextManual = pinnedProducts.filter((candidate) => `${candidate.id}` !== key);
    if (nextManual.length !== pinnedProducts.length) onChange(nextManual);
    if (!excludedIds.has(key)) onExcludedChange([...excludedProductIds, product.id]);
  }, [excludedIds, excludedProductIds, onChange, onExcludedChange, pinnedProducts]);

  const handleRestore = useCallback((id) => {
    onExcludedChange(excludedProductIds.filter((candidate) => `${candidate}` !== `${id}`));
  }, [excludedProductIds, onExcludedChange]);

  return (
    <div style={S.wrapper}>
      {!hideHeader && <h3>{t('empty.query.products.editor.title')}</h3>}

      <div style={S.searchArea}>
        <ProductSelector
          api={searchApi}
          pickedProducts={effectiveItems}
          onPick={handleAdd}
          onRemove={() => {}}
          limit={8}
          showPickedZone={false}
        />
      </div>

      <div style={S.toolbar}>
        <span style={S.toolbarSummary}>
          <span style={S.toolbarStrong}>{pinnedProducts.length}</span>
          {' '}{t('empty.query.collection.pinned')}
          {' · '}{effectiveItems.length} {t('empty.query.collection.visible')}
        </span>
        <span style={S.toolbarActions}>
          <AutoFillControl
            enabled={autoFill}
            limit={limit}
            unit={t('empty.query.products.editor.autofill.unit')}
            onChange={onAutoFillChange}
            showTarget={false}
          />
          {autoFill && (
            <label style={S.limitControl}>
              {t('empty.query.autofill.up.to.label')}
              <input
                type="number"
                min="1"
                max="100"
                value={limit}
                style={S.limitInput}
                aria-label={t('empty.query.form.limit')}
                onChange={(event) => {
                  const value = Math.min(Math.max(Number(event.target.value) || 1, 1), 100);
                  onLimitChange(value);
                }}
              />
              {t('empty.query.products.editor.autofill.unit')}
            </label>
          )}
        </span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="effective-products">
          {(provided) => (
            <div
              ref={(node) => {
                provided.innerRef(node);
                listMotionRef.current = node;
              }}
              {...provided.droppableProps}
              style={S.zone}
            >
              {effectiveItems.length === 0 && (
                <div style={S.emptyState}>{t('empty.query.products.editor.generated.empty')}</div>
              )}
              {effectiveItems.map((product, index) => (
                <Draggable
                  key={`product-${product.key}`}
                  draggableId={`product-${product.key}`}
                  index={index}
                >
                  {(drag, snapshot) => (
                    <div
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      {...drag.dragHandleProps}
                      onMouseEnter={() => setHoveredId(product.key)}
                      onMouseLeave={() => setHoveredId(null)}
                      data-collection-row
                      data-collection-key={product.key}
                      style={{
                        ...S.row,
                        ...drag.draggableProps.style,
                        ...(product.manual ? S.rowPinned : null),
                        ...(product.unresolved ? { background: '#fff9ec' } : null),
                        ...(hoveredId === product.key ? { background: '#fbfefd' } : null),
                        ...(snapshot?.isDragging ? S.rowDragging : null),
                      }}
                    >
                      {product.manual ? (
                        <span style={S.pinnedPosition}>{index + 1}</span>
                      ) : (
                        <div
                          style={S.dragHandle}
                          aria-label={t('empty.query.products.editor.drag')}
                        >
                          <DragIndicatorIcon style={S.dragIcon} aria-hidden="true" />
                        </div>
                      )}
                      <ProductThumb product={product} />
                      <div style={S.itemMain}>
                        <div style={S.itemName}>{product.name}</div>
                        <div style={S.itemMeta}>
                          {[product.vendorCode, `#${product.id}`, product.vendor].filter(Boolean).join(' · ')}
                        </div>
                        {product.unresolved && (
                          <span style={S.warningBadge}>{t('empty.query.products.editor.unresolved.badge')}</span>
                        )}
                      </div>
                      <span style={S.price}>{formatPrice(product.price)}</span>
                      <PinAction
                        pinned={product.manual}
                        label={t(
                          product.manual
                            ? 'empty.query.collection.unpin.item'
                            : 'empty.query.collection.pin.item',
                          { name: product.name },
                        )}
                        onClick={() => handlePin(product)}
                      />
                      <Tooltip title={t('empty.query.collection.block.item', { name: product.name })}>
                        <IconButton
                          size="small"
                          style={{ ...S.actionButton, opacity: hoveredId === product.key ? 1 : 0.55 }}
                          aria-label={t('empty.query.collection.block.item', { name: product.name })}
                          onClick={() => handleHide(product)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {excludedProductIds.length > 0 && (
        <div style={S.hiddenZone}>
          <div style={S.hiddenTitle}>{t('empty.query.collection.blocked')}</div>
          {excludedProductIds.map((id) => {
            const product = knownProducts.get(`${id}`);
            const name = product?.name || t('empty.query.products.editor.fallback', { id });
            return (
              <div key={`hidden-product-${id}`} style={S.hiddenMediaRow}>
                <ProductThumb product={product || { name, thumbnail: '' }} blocked />
                <span style={S.itemMain}>
                  <span title={name} style={S.hiddenText}>{name}</span>
                  <span style={{ ...S.itemMeta, display: 'block' }}>
                    {[product?.vendorCode, `#${id}`, product?.vendor].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <Tooltip title={t('empty.query.products.editor.restore.label', { name })}>
                  <IconButton
                    size="small"
                    style={S.actionButton}
                    aria-label={t('empty.query.products.editor.restore.label', { name })}
                    onClick={() => handleRestore(id)}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductsEditor;
